import * as Sentry from "@sentry/react";
import { mergeWith } from "lodash";
import { InfiniteData, QueryKey } from "react-query";
import { Timeslot } from "types";
import { queryClient } from "@/api/queryClient";
import { Page } from "@/hooks/helper";
import { Resource } from "@/types/Response";

export const updateResourceCache = <T extends Resource>(collection: string, data: T, queryKey?: QueryKey) => {
    const key = queryKey ?? [collection, data.id];
    queryClient.setQueryData<T>(key, (currentData) =>
        mergeWith({}, currentData, data, (objectValue, sourceValue) => {
            if (Array.isArray(objectValue)) {
                return sourceValue;
            }
        }),
    );
};

/**
 * Update the collection data in the cache by specifying the collection name and callback.
 * The callback provides the data from the cache and allows you to return the new data if changed.
 * If the original data is returned from the callback, the cache will not be updated.
 *
 * The update is checking for both regular and infinite collections. Please note that if the data
 * is found in the infinite collection, the callback may be called multiple times.
 */
export const updateCollectionByQueryKey = <T>(queryKey: QueryKey, callback: (data: T[]) => T[]) => {
    const queries = queryClient.getQueriesData<T[] | InfiniteData<Page<T>> | undefined>(queryKey);

    try {
        for (const [key, data] of queries) {
            if (!data) {
                continue;
            }

            if ("pages" in data) {
                // For infinite data, we need to iterate over each page in the cache.
                const updatedPages: typeof data.pages = [];
                let isChanged = false;

                for (const page of data.pages) {
                    if (isChanged) {
                        // If already changed, directly push the page without changes.
                        updatedPages.push(page);
                        continue;
                    }

                    // Apply the callback to update the page data.
                    const updatedPageData = callback(page.data);
                    updatedPages.push({ ...page, data: updatedPageData });

                    // Check if the data was actually changed.
                    if (!shallowEqualArrays(updatedPageData, page.data)) {
                        isChanged = true;
                    }
                }

                if (isChanged) {
                    queryClient.setQueryData<InfiniteData<Page<T>>>(key, { ...data, pages: updatedPages });
                }
            } else {
                const updatedData = callback(data);

                if (updatedData !== data) {
                    queryClient.setQueryData(key, updatedData);
                }
            }
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: { module: "updateCollectionByQueryKey" },
            extra: {
                debugInfo: "Error happened in updateCollectionByQueryKey",
            },
        });

        console.error(`Error happened in updateCollectionByQueryKey: ${error}`);
    }
};

const shallowEqualArrays = (array1: any[], array2: any[]): boolean => {
    return array1.length === array2.length && array1.every((item, index) => item === array2[index]);
};

export const getResourceByQueryKey = <T>(queryKey: QueryKey, timeslotId: ID) => {
    const queries = queryClient.getQueriesData<T[] | InfiniteData<Page<Timeslot>> | undefined>(queryKey);

    for (const [, data] of queries) {
        if (!data) {
            return false;
        }

        if ("pages" in data) {
            for (const page of data.pages) {
                if ("data" in page) {
                    const { data } = page;
                    const resource = data.find((itr) => itr.id === timeslotId);
                    if (resource) {
                        return resource;
                    }
                }
            }
        }
    }

    return false;
};
